import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { productsAPI } from '../utils/api';
import { useCart } from '../context/CartContext';
import { useTranslation } from 'react-i18next';
import { FaArrowLeft, FaPlus, FaMinus, FaShoppingCart } from 'react-icons/fa';
import '../styles/ProductDetails.css';

// Вспомогательная функция для округления до 1 знака после запятой
const roundToOne = (num) => Math.round(num * 10) / 10;

const ProductDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart, cart, updateQuantity } = useCart();
  const { t } = useTranslation();
  
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [quantity, setQuantity] = useState(1);

  const cartItem = cart.find(item => item.product._id === id && item.unit === 'piece');
  const cartQuantity = cartItem ? cartItem.quantity : 0;
  
  // Кратность для товаров с кг (минимальный шаг добавления)
  const multiplicity = product?.unit === 'kg' ? (product.multiplicity || 1) : 1;

  useEffect(() => {
    loadProduct();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadProduct = async () => {
    setLoading(true);
    try {
      const response = await productsAPI.getProduct(id);
      const productData = response.data.data;
      setProduct(productData);
      // Устанавливаем начальное количество в зависимости от кратности
      if (productData.unit === 'kg' && productData.multiplicity) {
        setQuantity(productData.multiplicity);
      }
    } catch (error) {
      console.error('Ошибка загрузки продукта:', error);
      setError('Не удалось загрузить продукт');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    addToCart(product, quantity, 'piece');
    // Сбрасываем на значение кратности для кг товаров
    setQuantity(multiplicity);
  };

  const handleIncrement = () => {
    // Для кг товаров увеличиваем на кратность с округлением
    const step = multiplicity;
    if (cartQuantity > 0) {
      updateQuantity(product._id, roundToOne(cartQuantity + step), 'piece');
    } else {
      setQuantity(prev => roundToOne(prev + step));
    }
  };

  const handleDecrement = () => {
    // Для кг товаров уменьшаем на кратность с округлением
    const step = multiplicity;
    if (cartQuantity > 0) {
      updateQuantity(product._id, Math.max(0, roundToOne(cartQuantity - step)), 'piece');
    } else {
      setQuantity(prev => Math.max(multiplicity, roundToOne(prev - step)));
    }
  };

  const getUnitLabel = (unit) => {
    switch (unit) {
      case 'kg': return t('kg');
      case 'piece': return t('piece');
      case 'box': return t('boxUnit');
      default: return unit;
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>{t('loading')}</p>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="error-container">
        <p>{error || t('productNotFound', 'Product not found')}</p>
        <button onClick={() => navigate('/catalog')} className="btn-primary">
          {t('backToCatalog', 'Back to catalog')}
        </button>
      </div>
    );
  }

  const price = product.finalPrice || product.price;
  
  // Получаем информацию о скидках
  const personalDiscount = product.personalDiscount || { type: null, value: 0, amount: 0 };
  const generalDiscountType = product.generalDiscountType || 'none';
  const generalDiscountValue = product.generalDiscountValue || 0;
  
  // Определяем, есть ли скидка
  const hasDiscount = (personalDiscount.value > 0 || (generalDiscountType !== 'none' && generalDiscountValue > 0)) && price < product.price;
  
  // Определяем, какая скидка фактически применена (какая дала лучшую цену)
  let discountDisplay = '';
  let appliedDiscountType = 'none'; // 'personal' или 'general'
  
  if (personalDiscount.value > 0 || (generalDiscountType !== 'none' && generalDiscountValue > 0)) {
    // Рассчитываем цены с каждой скидкой
    let priceWithPersonal = product.price;
    if (personalDiscount.type === 'percent') {
      priceWithPersonal = product.price * (1 - personalDiscount.value / 100);
    } else if (personalDiscount.type === 'fixed') {
      priceWithPersonal = product.price - personalDiscount.value;
    } else if (personalDiscount.type === 'fixed_price') {
      priceWithPersonal = personalDiscount.value;
    }
    
    let priceWithGeneral = product.price;
    if (generalDiscountType === 'percent' && generalDiscountValue > 0) {
      priceWithGeneral = product.price * (1 - generalDiscountValue / 100);
    } else if (generalDiscountType === 'fixed' && generalDiscountValue > 0) {
      priceWithGeneral = product.price - generalDiscountValue;
    } else if (generalDiscountType === 'fixed_price' && generalDiscountValue > 0) {
      priceWithGeneral = generalDiscountValue;
    }
    
    // Определяем, какая скидка применилась
    if (priceWithPersonal <= priceWithGeneral && personalDiscount.value > 0) {
      appliedDiscountType = 'personal';
      if (personalDiscount.type === 'percent') {
        discountDisplay = `${personalDiscount.value}%`;
      } else if (personalDiscount.type === 'fixed') {
        discountDisplay = `${personalDiscount.value.toFixed(2)}€/${t(`piece`, 'tk')}`;
      } else if (personalDiscount.type === 'fixed_price') {
        const discountAmount = product.price - personalDiscount.value;
        const discountPercent = ((discountAmount / product.price) * 100).toFixed(0);
        discountDisplay = `${discountPercent}%`;
      }
    } else if (generalDiscountType !== 'none' && generalDiscountValue > 0) {
      appliedDiscountType = 'general';
      if (generalDiscountType === 'percent') {
        discountDisplay = `${generalDiscountValue}%`;
      } else if (generalDiscountType === 'fixed') {
        discountDisplay = `${generalDiscountValue.toFixed(2)}€/${t(`piece`, 'tk')}`;
      } else if (generalDiscountType === 'fixed_price') {
        const discountAmount = product.price - generalDiscountValue;
        const discountPercent = ((discountAmount / product.price) * 100).toFixed(0);
        discountDisplay = `${discountPercent}%`;
      }
    }
  }

  return (
    <div className="product-details-page">
      <button onClick={() => navigate(-1)} className="back-btn">
        <FaArrowLeft /> {t('back', 'Back')}
      </button>

      <div className="product-details-container">
        <div className="product-image-section">
          <img 
            src={product.effectiveImageUrl || product.imageUrl || '/images/placeholder.jpg'} 
            alt={product.name}
            onError={(e) => {
              e.target.src = '/images/placeholder.jpg';
            }}
          />
          {hasDiscount && (
            <div className="discount-badge-large">-{discountDisplay}</div>
          )}
        </div>

        <div className="product-info-section">
          <h1>{product.name}</h1>

          <div className="product-meta">
            {product.category && (
              <span className="category-tag">
                {product.category.name}
                {product.subCategory && ` > ${product.subCategory}`}
              </span>
            )}
          </div>

          <div className="price-section">
            {hasDiscount && (
              <div className="original-price-large">
                {product.price.toFixed(2)} €
              </div>
            )}
            <div className="current-price-large">
              {price.toFixed(2)} € / {getUnitLabel(product.unit)}
            </div>
            {hasDiscount && (
              <div className="discount-label">
                {personalDiscount.value > 0 && generalDiscountType !== 'none' && generalDiscountValue > 0 ? (
                  <>
                    {appliedDiscountType === 'personal' ? (
                      <>
                        <div>t(`yourPersonalDiscountApplied`, 'Your personal discount is applied') {personalDiscount.type === 'percent' ? `${personalDiscount.value}%` : `${personalDiscount.value.toFixed(2)}€/` + t(`piece`, 'tk')}</div>
                        <small style={{ fontSize: '0.85rem', opacity: 0.8 }}>
                          (t(`generalDiscount`, 'General discount') {generalDiscountType === 'percent' ? `${generalDiscountValue}%` : `${generalDiscountValue.toFixed(2)}€/` + t(`piece`, 'tk')}.)
                        </small>
                      </>
                    ) : (
                      <>
                        <div>🎉 t(`generalDiscountApplied`, 'General discount is applied'): {generalDiscountType === 'percent' ? `${generalDiscountValue}%` : `${generalDiscountValue.toFixed(2)}€/` + t(`piece`, 'tk')}</div>
                        <small style={{ fontSize: '0.85rem', opacity: 0.8 }}>
                          (t(`yourPersonalDiscount`, 'Your personal discount') {personalDiscount.type === 'percent' ? personalDiscount.value + '%' : personalDiscount.value.toFixed(2) + '€/' + t(`piece`, 'tk')}.)
                        </small>
                      </>
                    )}
                  </>
                ) : appliedDiscountType === 'personal' ? (
                  personalDiscount.type === 'percent' 
                    ? `🎁 ` + t(`yourPersonalDiscount`, 'Your personal discount') + `: ${personalDiscount.value}%`
                    : `🎁 ` + t(`yourPersonalDiscount`, 'Your personal discount') + `: ${personalDiscount.value.toFixed(2)}€`
                ) : (
                  generalDiscountType === 'percent'
                    ? `🎉` + t(`discount`, 'Discount') + `: ${generalDiscountValue}%`
                    : `🎉` + t(`discount`, 'Discount') + `: ${generalDiscountValue}%`
                )}
              </div>
            )}
          </div>

          {product.sellByBox && (
            <div className="box-info-section">
              <h3>{t('boxSales', 'Box sales')}</h3>
              <p>
                {t('inBox', 'In box')}: {product.boxQuantity} {getUnitLabel(product.unit)}<br />
                {t('boxPrice', 'Box price')}: {product.boxPrice?.toFixed(2)} €
              </p>
            </div>
          )}

          <div className="add-to-cart-section">
            <div className="quantity-selector">
              <button 
                className="quantity-btn-large"
                onClick={handleDecrement}
              >
                <FaMinus />
              </button>
              {product.unit === 'kg' ? (
                // Для кг товаров - только отображение, без ручного ввода
                // Фиксированная ширина для чисел до 99.9 кг
                <span 
                  className="quantity-display-fixed"
                  style={{
                    width: '90px',
                    padding: '8px 12px',
                    textAlign: 'center',
                    fontSize: '1.1rem',
                    fontWeight: '500',
                    display: 'inline-block',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    background: '#f9f9f9'
                  }}
                >
                  {cartQuantity > 0 ? cartQuantity : quantity} {t('kg')}
                </span>
              ) : (
                // Для штучных товаров - обычный input
                <input 
                  type="number"
                  value={cartQuantity > 0 ? cartQuantity : quantity}
                  onChange={(e) => {
                    const val = parseInt(e.target.value) || 1;
                    if (cartQuantity > 0) {
                      updateQuantity(product._id, val, 'piece');
                    } else {
                      setQuantity(val);
                    }
                  }}
                  min="1"
                  className="quantity-input"
                />
              )}
              <button 
                className="quantity-btn-large"
                onClick={handleIncrement}
              >
                <FaPlus />
              </button>
            </div>

            {cartQuantity === 0 && (
              <button 
                className="add-to-cart-btn-large"
                onClick={handleAddToCart}
              >
                <FaShoppingCart /> {t('addToCart')}
              </button>
            )}
          </div>

          {cartQuantity > 0 && (
            <div className="in-cart-message">
              ✓ {t('inCart', 'In cart')}: {cartQuantity} {getUnitLabel(product.unit)}
              {product.unit === 'kg' && multiplicity > 1 && (
                <span style={{ fontSize: '0.85em', opacity: 0.8 }}>
                  {' '}({t('step', 'step')}: {multiplicity} {t('kg')})
                </span>
              )}
            </div>
          )}

          <div className="description-section">
            <h2>{t('description')}</h2>
            <p>{product.description || t('noDescription', 'No description available')}</p>
          </div>

          {product.nutrition && (
            <div className="nutrition-section">
              <h2>{t('nutritionalValue', 'Nutritional value')}</h2>
              <div className="nutrition-table">
                {product.nutrition.servingSize && (
                  <div className="nutrition-row">
                    <span>{t('servingSize', 'Serving size')}:</span>
                    <span>{product.nutrition.servingSize}</span>
                  </div>
                )}
                {product.nutrition.calories && (
                  <div className="nutrition-row">
                    <span>{t('calories', 'Calories')}:</span>
                    <span>{product.nutrition.calories} {t('caloriesUnit', 'calories unit')}</span>
                  </div>
                )}
                {product.nutrition.protein && (
                  <div className="nutrition-row">
                    <span>{t('protein', 'Protein')}:</span>
                    <span>{product.nutrition.protein} {t('g', 'g')}</span>
                  </div>
                )}
                {product.nutrition.fat && (
                  <div className="nutrition-row">
                    <span>{t('fat', 'Fat')}:</span>
                    <span>{product.nutrition.fat} {t('g', 'g')}</span>
                  </div>
                )}
                {product.nutrition.carbohydrates && (
                  <div className="nutrition-row">
                    <span>{t('carbohydrates', 'Carbohydrates')}:</span>
                    <span>{product.nutrition.carbohydrates} {t('g', 'g')}</span>
                  </div>
                )}
                {product.nutrition.fiber && (
                  <div className="nutrition-row">
                    <span>{t('fiber', 'Fiber')}:</span>
                    <span>{product.nutrition.fiber} {t('g', 'g')}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductDetails;

